// ── ASSEMBLING WHAT THE ADJUSTER NEEDS ───────────────────────────────────────
//
// `adjudicatePreventPlant` takes cells. This builds them, and it is ONE rule because the
// screen and tools/pp-adjuster-report.js both need it — a second assembly would answer
// questions about a determination subtly unlike the one on screen, which is the drift
// this repo has written down for three news handlers and two guarantee engines.
//
// Four traps are baked in here, each measured on the live CY2026 book:
//
//  1. ⚠ OVERLAY ROWS RESTATE THE SAME PHYSICAL ACRES. `crop_coverage_units` carries
//     SCO-RP and ECO-RP rows — 2,055 in CY2026 — so an acreage summed over the table
//     runs 2-3x high. Anchor's Erie corn reads 1,527.76 planted against a real 763.88:
//     the same figure twice. Only base plans count.
//  2. ⚠ THE PROJECTED PRICE IS PER (CROP, TYPE, PRACTICE). CY2026 NY corn is $4.62 grain
//     non-irrigated, $7.30 organic grain, $38.25 SILAGE and $60.50 organic silage — four
//     numbers under one commodity code. Keyed on the crop name alone this prices a silage
//     acre at a grain price.
//  3. ⚠ THE PRICE ELECTION AND THE PF OPTION LIVE ON THE COVERAGE, NOT THE UNIT. Without
//     the election a CAT acre prices at 1/0.55 = 1.82x what the carrier pays; without the
//     options `resolvePpFactor` answers "not captured" and returns a range, not a factor.
//  4. ⚠ ELIGIBILITY JOINS ON (POLICY, COUNTY, CROP) AND NOT ON TYPE. `pp_eligibility.type`
//     is free text — 38 spellings, 196 blank — so a type-level join would DROP rows rather
//     than sharpen them. Where one crop carries several eligibility types this takes the
//     largest, which is a KNOWN limit and is reported on the cell rather than hidden.

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const up = (s) => String(s == null ? "" : s).trim().toUpperCase();

// Base MPCI plans. An overlay is not a second planting.
export const BASE_PLANS = Object.freeze(["APH", "RP", "YP", "RP-HPE"]);
const isBasePlan = (p) => BASE_PLANS.indexOf(String(p || "")) >= 0;
// ⚠ THE ASSEMBLY MAY NOT DEPEND ON THE ORDER ROWS ARRIVE IN (2026-10-19). Several steps
// here are first-wins or tie-keep-first (a cell's practice and terms, the cell order the
// roll walks, the eligibility tie), and the screens read these tables through paged
// requests whose order is the database's business. Shuffling the live CY2026 units moved
// PALLADINO AND CARLEY's Onondaga soybean PP between $169.90 and $228.64 and WYOMING
// potatoes by $0.67, with no input changing. So rows are walked in ONE order: their
// primary key where the caller selected it, else their full content (rows identical in
// every selected column are interchangeable, so any order among them is the same answer).
const rowKey = (r) => (r && r.id != null ? "0" + String(r.id) : "1" + JSON.stringify(r));
export function stableOrder(rows) {
  return (rows || []).map((r) => [rowKey(r), r])
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)).map((x) => x[1]);
}
// `crop_coverage.mpci_plan_code` spells the same base plans as RMA plan codes.
export const BASE_COVERAGE_PLAN_CODES = new Set(["01", "02", "03", "90"]);

export const priceKey = (crop, type, practice) =>
  [up(crop), up(type) || "-", up(practice) || "-"].join("|");

// `rmaPrices` are `rma_prices` rows; `cropOf` maps a commodity code to a crop name (the
// caller passes RMA_CROPS so this module holds no second copy of that table).
//
// ⚠ A CELL PUBLISHED WITH TWO DIFFERENT PRICES IS DROPPED, not resolved by sort order.
// Taking whichever arrived first would price an acre off a row nobody chose.
export const stateKey = (stateFips, crop, type, practice) =>
  "S" + String(stateFips).trim().padStart(2, "0") + "|" + priceKey(crop, type, practice);

export function buildPriceIndex(rmaPrices = [], cropOf = () => null) {
  const px = new Map();
  for (const r of rmaPrices || []) {
    const crop = cropOf(String(r.commodity_code || "").padStart(4, "0"));
    const p = num(r.projected_price);
    if (!crop || !(p > 0)) continue;
    // ⚠ PER STATE TOO. RMA publishes soybeans 997/003 at $11.09 in New York and $11.19
    // elsewhere, so the state-blind key below is DROPPED as conflicting — which left every
    // soybean cell in the book unpriced (found 2026-10-19 on BAINBRIDGE and SILSBY, whose
    // soybean PP could neither be determined nor lend). The state key answers first; the
    // blind key keeps its old meaning (one price everywhere, or none).
    const keys = [priceKey(crop, r.type_code, r.practice_code)];
    if (r.state_code != null && String(r.state_code).trim() !== "")
      keys.push(stateKey(r.state_code, crop, r.type_code, r.practice_code));
    for (const k of keys) {
      if (px.has(k)) { if (px.get(k) !== p) px.set(k, null); continue; }
      px.set(k, p);
    }
  }
  return px;
}

// Returns a Map of policy_id -> { policy, cells[] }. Cells carry everything
// `adjudicatePreventPlant` reads plus what a screen needs to show its working.
export function buildPpCells({
  units = [], coverages = [], eligibility = [], policies = [], priceIndex = new Map(),
  cropYear = null,
  // Optional: maps a policy's state (`NY`) to RMA's FIPS (`36`) so a price can be read per
  // state. The caller passes `ppStateFips` (src/ppFactor.js); absent, prices are state-blind.
  stateFipsOf = null,
} = {}) {
  const P = new Map((policies || []).map((p) => [p.id, p]));

  // Coverage-level terms: the election and the elected options.
  // ⚠ "TYPE NOT STATED" HAS THREE SPELLINGS. The sync writes a unit with no type as `997`;
  // the same coverage's `crop_coverage.type_code` is NULL. Joined literally, a soybean unit
  // never found its own coverage — no election, no options — and was left unpriced.
  const tk = (t) => { const v = up(t); return (v === "997" || v === "000") ? "" : v; };
  // ⚠ THE BASE COVERAGE ANSWERS, NEVER THE SCO/ECO OVERLAY BESIDE IT (2026-10-19).
  // `crop_coverage` holds the base line AND its overlays under the same (policy, county,
  // crop, type) — Voelpel's Niagara soybeans are plan 02 + 32 + 88 — and the overlays
  // carry NO elected options (0 of 948 CY2026 overlay rows) and their own iif_key. This
  // was `covBy.set(...)`, LAST WINS, so whichever row the read happened to return last
  // decided the cell: an overlay left `options` null, `resolvePpFactor` answered "not
  // captured", and the soybean PP was held as "no factor or projected price". The number
  // of held crops moved 7 to 19 with nothing but row order (measured by shuffling the
  // live CY2026 inputs). Rank, not arrival: base plan > no plan stated (hand-loaded) >
  // overlay; a tie goes to the row that states options, then to the lower id.
  const covRank = (c) => {
    const pc = String(c.mpci_plan_code == null ? "" : c.mpci_plan_code).trim();
    if (pc === "") return 1;
    return BASE_COVERAGE_PLAN_CODES.has(pc.padStart(2, "0")) ? 2 : 0;
  };
  const hasOpts = (c) => (c.insurance_options != null && String(c.insurance_options).trim() !== "") ? 1 : 0;
  const covBeats = (a, b) => (covRank(a) - covRank(b)) || (hasOpts(a) - hasOpts(b))
    || (String(a.id == null ? "" : a.id) < String(b.id == null ? "" : b.id) ? 1 : -1);
  const covBy = new Map();
  for (const c of coverages || []) {
    const k = [c.policy_id, up(c.county), up(c.crop), tk(c.type_code)].join("|");
    const cur = covBy.get(k);
    if (!cur || covBeats(c, cur) > 0) covBy.set(k, c);
  }

  // ⚠ LARGEST WINS WHERE ONE CROP CARRIES SEVERAL ELIGIBILITY TYPES. Anchor's Erie corn
  // is GSG 605.46 and GSS 170; with no joinable type the alternative is to pick by
  // arrival order, which is the first-wins defect this file's neighbours already record.
  // The count is carried on the cell so a screen can say the choice was made.
  const eligBy = new Map();
  for (const e of stableOrder(eligibility)) {
    const k = [e.policy_id, up(e.county), up(e.crop)].join("|");
    const cur = eligBy.get(k);
    if (!cur) { eligBy.set(k, { row: e, n: 1 }); continue; }
    cur.n++;
    const a = num(e.remaining_acres) != null ? num(e.remaining_acres) : num(e.max_acres);
    const b = num(cur.row.remaining_acres) != null ? num(cur.row.remaining_acres) : num(cur.row.max_acres);
    if ((a || 0) > (b || 0)) cur.row = e;
  }

  const byPolicy = new Map();
  for (const u of stableOrder(units)) {
    if (!isBasePlan(u.plan)) continue;
    const p = P.get(u.policy_id);
    if (!p) continue;
    if (cropYear != null && Number(p.crop_year) !== Number(cropYear)) continue;

    let g = byPolicy.get(u.policy_id);
    if (!g) { g = { policy: p, cells: new Map() }; byPolicy.set(u.policy_id, g); }

    const ck = [up(u.county), up(u.crop), up(u.type_code)].join("|");
    let c = g.cells.get(ck);
    if (!c) {
      c = {
        county: u.county, crop: u.crop, type: u.type_code, practice: u.practice_code,
        ppAcres: 0, plantedAcres: 0, aipLiability: 0, uninsurablePpAcres: 0, ppUnits: [], plantedReported: false,
        aph: null, coverageLevel: null, share: null,
        options: null, priceElectionPct: null, eligRow: null, eligTypes: 0,
        // ⚠ THE COVERAGE KEY, AND A FROZEN ESTIMATE IS WORTHLESS WITHOUT IT. `m13_loss_detail`
        // carries no policy_id; what every row does carry is the AIP Insurance In Force key,
        // which `crop_coverage.iif_key` carries too. It is how a payment is matched back to
        // the estimate that preceded it — without it the comparison could only be made on
        // crop year and stage, which is the whole book.
        iifKey: null,
      };
      g.cells.set(ck, c);
    }
    // ⚠ WHICH BUCKET A UNIT IS IN COMES FROM THE SYNC'S OWN ACREAGE RULE, NOT FROM HERE.
    // `is_prevent_plant` is `isPreventPlantStatus(P11 f[37])` — P and E — AND NOT
    // uninsurable (P11 f[22]); L and M are planted acres with a reduced guarantee. So the
    // one case left to decide is a line the AIP coded prevented (P/E) that is ALSO
    // uninsurable: it carries no guarantee on this crop line, so it is neither a prevented
    // acre to pay nor a planted acre that reduces eligibility (¶26C(4) subtracts acres that
    // were PLANTED). It goes in neither bucket and is counted. (Measured CY2026: 14 such
    // lines, all already 0.00 acres after the sync's uninsurable split — so this changes no
    // figure today; it stops one the day the split leaves acres on them.)
    const st = up(u.acreage_status);
    const ppCoded = st === "P" || st === "E";
    if (u.is_prevent_plant) {
      const a = num(u.acres) || 0;
      c.ppAcres += a; c.aipLiability += num(u.liability) || 0;
      // Per UNIT, because one cell spans several units with their own APH, level and
      // share — Krenzer's Monroe corn is 320.5 ac at 193 bu and 39.7 ac at 202. A single
      // cell APH priced all 360.2 ac at one of them and read $5,479 high against what the
      // carrier paid; the carrier prices per unit, so the engine does too.
      c.ppUnits.push({
        acres: a, aph: num(u.aph_yield), coverageLevel: num(u.coverage_level),
        share: num(u.share), status: st || null, practice: u.practice_code,
      });
    } else if (ppCoded) {
      c.uninsurablePpAcres += num(u.acres) || 0;
    } else {
      c.plantedAcres += num(u.acres) || 0;
      // Whether the acreage report states a planted figure at all. A unit row with NULL acres
      // is a shell (wheat coded Z, "not planted on the entire unit", reaches us that way),
      // and a cell made only of shells says nothing positive about what was planted.
      if (num(u.acres) != null) c.plantedReported = true;
    }

    // A prevent-plant unit's own terms win; otherwise take whatever states them.
    if (u.is_prevent_plant || c.aph == null) {
      if (num(u.aph_yield) > 0) c.aph = num(u.aph_yield);
      if (num(u.coverage_level) > 0) c.coverageLevel = num(u.coverage_level);
      if (num(u.share) > 0) c.share = num(u.share);
      if (u.practice_code != null) c.practice = u.practice_code;
    }

    const cov = covBy.get([u.policy_id, up(u.county), up(u.crop), tk(u.type_code)].join("|"));
    if (cov) {
      if (c.options == null && cov.insurance_options != null
          && String(cov.insurance_options).trim() !== "") c.options = cov.insurance_options;
      if (c.priceElectionPct == null) c.priceElectionPct = num(cov.price_election_pct);
      if (c.iifKey == null && cov.iif_key != null && String(cov.iif_key).trim() !== "")
        c.iifKey = String(cov.iif_key).trim();
    }
    const el = eligBy.get([u.policy_id, up(u.county), up(u.crop)].join("|"));
    if (el) { c.eligRow = el.row; c.eligTypes = el.n; }
  }

  for (const g of byPolicy.values()) {
    g.cells = [...g.cells.values()];
    for (const c of g.cells) {
      const fips = typeof stateFipsOf === "function" ? stateFipsOf(g.policy.state) : null;
      const look = (crop, type, practice) => {
        if (fips) { const v = priceIndex.get(stateKey(fips, crop, type, practice)); if (v != null) return v; }
        return priceIndex.get(priceKey(crop, type, practice)) ?? null;
      };
      c.price = look(c.crop, c.type, c.practice);
      // ⚠ AND EACH PREVENTED UNIT AT ITS OWN PRACTICE'S PRICE. One (county, crop, type) can
      // hold an organic unit beside a conventional one — PALLADINO AND CARLEY's Onondaga
      // corn priced every acre at the organic $7.30 off whichever unit set the cell's
      // practice, and read $28,671 against $18,372 paid.
      for (const u of c.ppUnits) u.price = look(c.crop, c.type, u.practice);
    }
  }
  return byPolicy;
}

// The `model` the adjuster asks for, built from a price index and a factor resolver the
// CALLER supplies. `resolvePpFactor` lives in src/ppFactor.js and App.jsx owns it; a copy
// here is how two factor ladders come to disagree.
//
// ⚠ `acreGuaranteeQty` IS NEVER PASSED TO THE RESOLVER. It is the AIP's own stated
// guarantee — rung 1 of the ladder — and a determination built on it agrees with the
// carrier by construction. The adjuster refuses that basis anyway; withholding it here
// means the refusal is never even reached on a real cell.
export function buildModel({ cells = [], resolveFactor, cropYear = null, state = null } = {}) {
  const find = (crop, type) => cells.find((x) => up(x.crop) === crop && up(x.type) === type);
  return {
    factorFor: (crop, type) => {
      const c = find(crop, type);
      if (typeof resolveFactor !== "function") return { factor: null, basis: null };
      const r = resolveFactor({
        crop, cropYear, state,
        county: c ? c.county : null,
        practiceCode: c ? c.practice : null,
        options: c ? c.options : null,
        optionsCaptured: !!(c && c.options != null),
      });
      // ⚠ ONLY AN EXACT FACTOR PRICES AN ACRE. A range is an honest answer to "what
      // factor does this carry" and is not a rate.
      return { factor: r && r.exact ? r.factor : null, basis: r ? r.basis : null };
    },
    priceFor: (crop, type) => {
      const c = find(crop, type);
      const p = c ? c.price : null;
      return { price: p ?? null, basis: p != null ? "rma_published" : null };
    },
  };
}
